import { useState } from "react";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Bell, Sparkles } from "lucide-react";

/**
 * WaitlistGate — the "coming soon + email capture" screen shown wherever a
 * new-patient signup CTA would normally land. Triggered globally by the
 * super-admin's Waitlist Mode toggle (Settings → System Ops).
 *
 * Renders as a stand-alone page block (self-contained styling) so it can be
 * dropped into any signup entry point (PatientRegister, PatientOnboarding,
 * fit-quiz final step, deep-linked /register bookmarks).
 */
const API = process.env.REACT_APP_BACKEND_URL || "";

export default function WaitlistGate({ message, source = "signup_page" }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e?.preventDefault?.();
    setError("");
    const cleaned = email.trim().toLowerCase();
    if (!cleaned || !cleaned.includes("@")) {
      setError("Please enter a valid email address.");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/api/system/waitlist-signup`, {
        email: cleaned, name: name.trim(), source,
      });
      setDone(true);
    } catch (e) {
      setError(e?.response?.data?.detail === "invalid_email"
        ? "That email doesn't look right — check the typo?"
        : "We couldn't save that. Please try again in a moment.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="waitlist-gate"
      className="min-h-screen w-full flex items-center justify-center px-4 py-12 bg-gradient-to-br from-slate-900 via-purple-950 to-slate-900"
    >
      <div className="max-w-lg w-full">
        <div className="rounded-3xl border border-purple-800/50 bg-white/95 dark:bg-slate-900/95 backdrop-blur p-8 sm:p-10 shadow-2xl">
          <div className="flex items-center justify-center w-14 h-14 rounded-2xl bg-purple-600/15 mx-auto mb-5">
            <Sparkles className="w-7 h-7 text-purple-600 dark:text-purple-400" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white text-center mb-3">
            Almost ready.
          </h1>
          <p className="text-sm sm:text-base text-slate-600 dark:text-slate-300 text-center leading-relaxed mb-8">
            {message || "We're not dispensing yet — but we're almost ready! Drop your email below and we'll notify you the moment we start accepting new patients."}
          </p>

          {done ? (
            <div className="rounded-2xl bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-900/50 p-5 text-center" data-testid="waitlist-success">
              <CheckCircle2 className="w-10 h-10 text-emerald-600 mx-auto mb-2" />
              <p className="text-base font-semibold text-emerald-900 dark:text-emerald-200">
                You&apos;re on the list.
              </p>
              <p className="text-xs text-emerald-800 dark:text-emerald-300 mt-1">
                We&apos;ll email you the moment we open the doors.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3">
              <div>
                <label className="text-xs font-medium text-slate-600 dark:text-slate-300 uppercase tracking-wider">
                  Your name (optional)
                </label>
                <Input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Jane"
                  className="mt-1 rounded-xl bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700"
                  data-testid="waitlist-name-input"
                  autoComplete="given-name"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 dark:text-slate-300 uppercase tracking-wider">
                  Email address
                </label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="mt-1 rounded-xl bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700"
                  required
                  data-testid="waitlist-email-input"
                  autoComplete="email"
                />
              </div>
              {error && (
                <p className="text-xs text-rose-600 dark:text-rose-400" data-testid="waitlist-error">{error}</p>
              )}
              <Button
                type="submit"
                disabled={saving || !email}
                className="w-full rounded-xl bg-purple-600 hover:bg-purple-700 text-white py-6 text-base font-semibold"
                data-testid="waitlist-submit"
              >
                <Bell className="w-4 h-4 mr-2" />
                {saving ? "Adding…" : "Notify me when you launch"}
              </Button>
              <p className="text-[11px] text-slate-500 dark:text-slate-400 text-center pt-2">
                No commitment. We&apos;ll only email you when we open enrollment.
              </p>
            </form>
          )}
        </div>
        <p className="text-xs text-slate-400 text-center mt-6">
          Already have an account?{" "}
          <a href="/login" className="text-purple-300 hover:text-purple-200 underline underline-offset-2">
            Sign in
          </a>
        </p>
      </div>
    </div>
  );
}
